import { useEffect, useId, useRef } from "react";

export function PrivyVoteConfirmationModal({
  open,
  details,
  onApprove,
  onCancel,
  title = "Confirm vote",
  description = "Review this vote before signing.",
  approveLabel = "Approve vote",
  cancelLabel = "Cancel"
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef(null);
  const rows = Array.isArray(details?.rows) && details.rows.length > 0
    ? details.rows
    : [
      { label: "Option", value: details?.optionLabel },
      { label: "Amount", value: details?.voteAmountText },
      { label: "Network", value: details?.networkLabel },
      { label: "Fee", value: details?.feeText },
      { label: "Wallet", value: details?.walletLabel }
    ];

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => dialogRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    function onKeyDown(event) {
      if (event.key === "Escape") {
        onCancel();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll("button,[tabindex]:not([tabindex='-1'])");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4 py-6">
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className="w-full max-w-md rounded-lg border border-zinc-700 bg-zinc-950 p-5 shadow-2xl outline-none"
      >
        <div className="space-y-4">
          <div className="space-y-1">
            <h2 id={titleId} className="text-lg font-semibold text-zinc-50">{title}</h2>
            <p id={descriptionId} className="text-sm leading-6 text-zinc-300">
              {description}
            </p>
          </div>
          <dl className="grid gap-2 rounded-md border border-zinc-800 bg-zinc-900/70 p-3 text-sm">
            {rows.map((row, index) => (
              <ConfirmationRow key={`${row.label}-${index}`} label={row.label} value={row.value} />
            ))}
          </dl>
          <div className="grid gap-2 sm:grid-cols-2">
            <button type="button" className="theme-button-primary" onClick={onApprove}>
              {approveLabel}
            </button>
            <button type="button" className="theme-button-secondary" onClick={onCancel}>
              {cancelLabel}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function ConfirmationRow({ label, value }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-2">
      <dt className="text-zinc-400">{label}</dt>
      <dd className="min-w-0 break-words text-right font-medium text-zinc-100">{value || "-"}</dd>
    </div>
  );
}
